import './ScanButton.scss';

import cn from 'classnames';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Button from '@/common/components/atoms/Button/Button';
import { ENDPOINT_PRODUCTS_QUICK_ENTRY, ROUTE_CARTS } from '@/common/constants';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import { ReactComponent as ScanSvg } from '@/ebat/assets/icons/scan.svg';
import Scandit from '@/ebat/components/blocks/Modal/Common/Scandit/Scandit';
import { useHeaderAccessesContext } from '@/ebat/contexts/Header/HeaderAccessesContext/HeaderAccessesContext';
import useCart from '@/ebat/hooks/carts/useCart';
import useUpdateQuickEntry from '@/ebat/mutations/products/quick-entry/useUpdateQuickEntry';
import { ROUTE_CART } from '@/materiaux/constants';

import {
  ScanButtonDefaultProps,
  ScanButtonPropTypes,
} from './ScanButton.proptypes';

const ScanButton = ({ className }) => {
  const location = useLocation();
  const { push } = useHistory();
  const isCartPage = location.pathname.includes(ROUTE_CART);
  const { defaultCart, refetchDefaultCart } = useDefaultCartContext();
  const { id } = useParams();
  const { refetch: refetchCart } = useCart({
    id,
    disabled: !isCartPage,
  });
  const { cartsScandit: hasScanditAccesses } = useHeaderAccessesContext();
  const { mutate: mutateUpdateQuickEntry } = useUpdateQuickEntry([
    ENDPOINT_PRODUCTS_QUICK_ENTRY,
  ]);
  const handleUpdateQuickEntry = (term, onError, onSuccess) =>
    mutateUpdateQuickEntry({ term }, { onError, onSuccess });

  const { addToCart } = useAddToContext();
  const {
    onCloseModal,
    onOpenModal,
    setModalClassName,
    setModalContent,
  } = useModalContext();

  const handleCartNavigation = () => {
    if (!isCartPage && defaultCart) {
      onCloseModal();
      push(`${ROUTE_CARTS}/${defaultCart?.id}`);
    }

    if (isCartPage && defaultCart) {
      onCloseModal();
    }
  };

  const handleScanResult = (scanResult) => {
    if (scanResult.barcodes.length > 0) {
      scanResult.barcodes.forEach((barcode) =>
        handleUpdateQuickEntry(barcode.data, null, (items) =>
          addToCart({
            items: [
              {
                brand: items[0].brand,
                name: items[0].name,
                quantity:
                  items[0].sales_packaging && !items[0].split_packaging
                    ? items[0].sales_packaging
                    : 1,
                sku: items[0].sku || items[0].main_sku,
              },
            ],
            cart: { ...defaultCart },
            onSuccessCallback: () => {
              refetchDefaultCart();
              if (isCartPage) {
                refetchCart().catch(() => null);
              }
            },
          }),
        ),
      );
    }
  };

  const handleOpenScandit = () => {
    setModalContent(
      <Scandit
        onScanResult={handleScanResult}
        handleUpdateQuickEntry={handleUpdateQuickEntry}
        onValidatePopin={(items) =>
          addToCart({
            items: [
              {
                brand: items[0].brand,
                name: items[0].name,
                quantity: items[0].quantity || 1,
                sku: items[0].sku || items[0].main_sku,
                source: items[0].source,
              },
            ],
            cart: { ...defaultCart },
            options: {
              linkAction: handleCartNavigation,
            },
            onSuccessCallback: () => {
              refetchDefaultCart();
              if (isCartPage) {
                refetchCart().catch(() => null);
              }
            },
          })
        }
      />,
    );
    setModalClassName('modal-scandit');
    onOpenModal();
  };

  return (
    <Button
      className={cn(className, 'scan-button', {
        'scan-button--disabled': !hasScanditAccesses,
      })}
      disabled={!hasScanditAccesses}
      onClick={handleOpenScandit}
    >
      <ScanSvg className="scan-button__icon" />
    </Button>
  );
};

ScanButton.defaultProps = {
  ...ScanButtonDefaultProps,
};

ScanButton.propTypes = {
  ...ScanButtonPropTypes,
};

export default ScanButton;
